/* ============================================================
   ILS — First-party behavior tracker (TASK-2545, frontend part 2/2)

   Vanilla JS, IIFE, zero deps. Feeds ils-admin `/api/track/*` (the SAME
   backend the email-capture scripts use — alert-dedication-staging).

   Design constraints (from the TASK-2541 production-conflict audit):
     - `window.fetch` is ALREADY wrapped once (custom-add-toast.js). Our
       wrapper captures the CURRENT window.fetch at install time and calls
       it via .apply, reads bodies only via resp.clone(), never alters the
       response, and wraps ALL of our own logic in try/catch. Order-
       independent chaining: whoever installs last wraps the earlier.
     - A head script unregisters every service worker on each load → we use
       navigator.sendBeacon with a fetch({keepalive:true}) fallback only.
     - Gift logic (custom-gift-logic.js + Cartbot) programmatically adds /
       removes the free "dried mango" gift (SKU DF021F, 5 variant ids). We
       EXCLUDE those automatic cart mutations from tracker events.
     - Storage: cookie `ils_vid`, localStorage `ils_vid` + `ils_consent`,
       sessionStorage prefix `ils_trk_`. Zero overlap with the pre-existing
       sandboxed pixel (cookie/ls `ils_uid`, endpoint /api/track) and with
       the theme's other keys (ils_recently_viewed, ils_search_sid,
       ils_eop_*, ils_leader_confetti_fired, ils.giftLogic.off).

   Consent gate is HARD: nothing is tracked unless localStorage.ils_consent
   grants analytics. If not granted we attach only a listener for our own
   `ils:consent` event (dispatched by ils-consent.js) to initialize later.

   Everything is fail-silent: no network error, missing endpoint (backend
   deploy pending → 404), or malformed page data may ever block or alter the
   UI. All tracker logic lives inside try/catch.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- config ---------- */
  // SAME base URL constant the email-capture scripts use
  // (custom-email-forms.js / custom-email-popup.js → ils-admin).
  var API_BASE = 'https://alert-dedication-production.up.railway.app';
  var BATCH_ENDPOINT = API_BASE + '/api/track/batch';
  var IDENTIFY_ENDPOINT = API_BASE + '/api/track/identify';

  var SESSION_TTL_MS = 30 * 60 * 1000; // rotate session after 30 min inactivity
  var FLUSH_INTERVAL_MS = 5000;
  var FLUSH_AT_COUNT = 10;
  var MAX_EVENTS_PER_BATCH = 50; // backend cap
  var MAX_BODY_BYTES = 32000;    // backend cap (32KB) — keep a small margin

  // Backend whitelist (unknown types are dropped server-side anyway; we also
  // gate client-side so we never waste a batch slot on a typo).
  var WHITELIST = {
    page_view: 1, page_leave: 1, locale_switch: 1, product_view: 1,
    collection_view: 1, add_to_cart: 1, remove_from_cart: 1,
    cart_qty_change: 1, checkout_started: 1, checkout_progress: 1,
    checkout_completed: 1, email_captured: 1, consent_changed: 1,
    // TASK-4916 — the delivery funnel step between add_to_cart and
    // checkout_started, plus the delivery page's own upsell block. The BACKEND
    // enum (ils-admin db_tracker.js EVENT_TYPES) carries the SAME three names;
    // a type missing from either side is dropped silently, per event, with a
    // 200 response — so the two lists have to be kept in step.
    delivery_reached: 1,
    delivery_upsell_impression: 1,
    delivery_upsell_add_to_cart: 1
  };

  // Gift variant ids to EXCLUDE from cart events — copied from
  // custom-gift-logic.js (GIFT_VARIANT_BY_GRAMS). All share SKU DF021F.
  var GIFT_SKU = 'DF021F';
  var GIFT_VARIANT_SET = {
    49820818047317: 1, 49820818080085: 1, 49820818112853: 1,
    52303207137621: 1, 52303207170389: 1
  };

  var PAGE = (window && window.ILS_PAGE) || {};
  var CUR_LOCALE = PAGE.locale || window.ILS_LANG || '';

  /* ---------- tiny utils ---------- */
  function uuid() {
    try {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
      if (window.crypto && crypto.getRandomValues) {
        var b = crypto.getRandomValues(new Uint8Array(16));
        b[6] = (b[6] & 0x0f) | 0x40;
        b[8] = (b[8] & 0x3f) | 0x80;
        var h = [];
        for (var i = 0; i < 16; i++) h.push((b[i] + 0x100).toString(16).slice(1));
        return h[0] + h[1] + h[2] + h[3] + '-' + h[4] + h[5] + '-' + h[6] + h[7] +
               '-' + h[8] + h[9] + '-' + h[10] + h[11] + h[12] + h[13] + h[14] + h[15];
      }
    } catch (e) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function nowIso() { try { return new Date().toISOString(); } catch (e) { return ''; } }

  function byteLen(s) { try { return new Blob([s]).size; } catch (e) { return (s && s.length) || 0; } }

  function getCookie(name) {
    try {
      var re = new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/+^])/g, '\\$1') + '=([^;]*)');
      var m = document.cookie.match(re);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  function setCookie(name, value, days) {
    try {
      var maxAge = Math.round(days * 24 * 60 * 60);
      var secure = (location.protocol === 'https:') ? '; Secure' : '';
      document.cookie = name + '=' + encodeURIComponent(value) +
        '; path=/; max-age=' + maxAge + '; SameSite=Lax' + secure;
    } catch (e) {}
  }

  /* ---------- consent ---------- */
  function readConsent() {
    try {
      var raw = localStorage.getItem('ils_consent');
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) { return null; }
  }
  function analyticsGranted() {
    var c = readConsent();
    return !!(c && c.analytics === true);
  }

  /* ---------- identity ---------- */
  var visitorId = null;
  var sessionId = null;

  function ensureVisitorId() {
    if (visitorId) return visitorId;
    var fromLs = null;
    try { fromLs = localStorage.getItem('ils_vid'); } catch (e) {}
    var fromCookie = getCookie('ils_vid');
    var vid = fromLs || fromCookie || uuid();
    // read-repair: keep localStorage and the first-party cookie in sync so
    // the checkout pixel (browser.cookie.get('ils_vid')) sees the same id.
    try { if (localStorage.getItem('ils_vid') !== vid) localStorage.setItem('ils_vid', vid); } catch (e) {}
    if (fromCookie !== vid) setCookie('ils_vid', vid, 400);
    visitorId = vid;
    return vid;
  }

  function ensureSession() {
    var now = Date.now();
    var sid = null, last = 0;
    try {
      sid = sessionStorage.getItem('ils_trk_sid');
      last = parseInt(sessionStorage.getItem('ils_trk_last'), 10) || 0;
    } catch (e) {}
    if (!sid || !last || (now - last) > SESSION_TTL_MS) {
      sid = uuid();
      try {
        sessionStorage.setItem('ils_trk_sid', sid);
        sessionStorage.removeItem('ils_trk_meta_sent'); // new session → resend meta
      } catch (e) {}
    }
    try { sessionStorage.setItem('ils_trk_last', String(now)); } catch (e) {}
    sessionId = sid;
    return sid;
  }

  function metaSent() { try { return sessionStorage.getItem('ils_trk_meta_sent') === '1'; } catch (e) { return false; } }
  function markMetaSent() { try { sessionStorage.setItem('ils_trk_meta_sent', '1'); } catch (e) {} }

  function deviceType() {
    try {
      var ua = navigator.userAgent || '';
      var w = window.innerWidth || document.documentElement.clientWidth || 0;
      if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
      if (/Mobi|iPhone|iPod|Android.*Mobile|Windows Phone/i.test(ua)) return 'mobile';
      if (w > 0 && w < 600) return 'mobile';
      if (w >= 600 && w <= 1024) return 'tablet';
      return 'desktop';
    } catch (e) { return 'desktop'; }
  }

  function parseUtm() {
    try {
      var q = location.search || '';
      if (!q) return null;
      var params = new URLSearchParams(q);
      var map = [['utm_source', 'source'], ['utm_medium', 'medium'], ['utm_campaign', 'campaign'], ['utm_term', 'term'], ['utm_content', 'content']];
      var o = {}, any = false;
      for (var i = 0; i < map.length; i++) {
        var v = params.get(map[i][0]);
        if (v) { o[map[i][1]] = v; any = true; }
      }
      return any ? o : null;
    } catch (e) { return null; }
  }

  function buildMeta() {
    var m = {};
    try { m.user_agent = navigator.userAgent || ''; } catch (e) {}
    try { m.accept_language = (navigator.languages && navigator.languages[0]) || navigator.language || ''; } catch (e) {}
    try { m.device_type = deviceType(); } catch (e) {}
    try { m.entry_url = location.href; } catch (e) {}
    try { m.referrer = document.referrer || ''; } catch (e) {}
    var utm = parseUtm();
    if (utm) m.utm = utm;
    return m;
  }

  /* ---------- queue + transport ---------- */
  var queue = [];
  var flushTimer = null;

  function track(type, payload, opts) {
    try {
      if (!WHITELIST[type]) return;
      ensureSession(); // refresh activity ts + rotate if stale
      queue.push({
        client_event_id: uuid(),
        event_type: type,
        occurred_at: nowIso(),
        url: location.href,
        locale: CUR_LOCALE,
        payload: payload || {}
      });
      if (opts && opts.beacon) { flush(true); }
      else if (opts && opts.immediate) { flush(false); }
      else if (queue.length >= FLUSH_AT_COUNT) { flush(false); }
    } catch (e) {}
  }

  function cleanEvents(events) {
    // Strip internal bookkeeping fields (e.g. __retried) before sending.
    var out = [];
    for (var i = 0; i < events.length; i++) {
      var e = events[i];
      out.push({
        client_event_id: e.client_event_id,
        event_type: e.event_type,
        occurred_at: e.occurred_at,
        url: e.url,
        locale: e.locale,
        payload: e.payload
      });
    }
    return out;
  }

  function buildBody(events, includeMeta) {
    var body = { visitor_id: visitorId, session_id: sessionId, events: cleanEvents(events) };
    if (includeMeta) body.meta = buildMeta();
    return JSON.stringify(body);
  }

  function flush(useBeacon) {
    try {
      if (!queue.length) return;
      if (!visitorId) ensureVisitorId();
      if (!sessionId) ensureSession();

      var batch = queue.splice(0, MAX_EVENTS_PER_BATCH);
      var includeMeta = !metaSent();
      var json = buildBody(batch, includeMeta);

      // 32KB guard — if the (possibly meta-laden) body is too big, push half
      // back onto the front of the queue and shrink until it fits.
      while (byteLen(json) > MAX_BODY_BYTES && batch.length > 1) {
        var half = Math.ceil(batch.length / 2);
        var back = batch.slice(half);
        batch = batch.slice(0, half);
        queue = back.concat(queue);
        json = buildBody(batch, includeMeta);
      }

      if (includeMeta) markMetaSent();
      send(json, batch, useBeacon);
    } catch (e) {}
  }

  function send(json, batch, useBeacon) {
    var beaconed = false;
    if (useBeacon) {
      try {
        if (navigator.sendBeacon) {
          /* TASK-5104 — THE TRANSPORT BUG, FIXED.
             This Blob used to be typed 'application/json'. That makes the
             beacon a NON-SIMPLE cross-origin request, so the browser has to
             send an OPTIONS preflight first — and when the page is navigating
             away (which is the only reason useBeacon is ever true) the
             preflight is dropped and the POST never leaves. sendBeacon still
             returns true, so `beaconed` short-circuits the fetch fallback
             below and the events are lost in complete silence, with no console
             error and no failed request to see.

             MEASURED COST: zero checkout_started rows in tracker_events from
             2026-07-22 to 2026-10-03, against 138 577 page_view rows sent over
             the ordinary fetch path. page_leave, same beacon path but usually
             fired without a navigation, managed only 583.

             'text/plain;charset=UTF-8' is one of the three CORS-safelisted
             request content types, so the request is SIMPLE and needs no
             preflight at all. The body is the identical JSON string; only the
             header changes. ils-admin v1.0.218 reads it with a per-route
             express.text parser on POST /api/track/batch and feeds the same
             handler — the same topology TASK-4939's delivery bridge has used in
             production since 2026-09 for exactly this reason.

             DEPLOY ORDER IS LOAD-BEARING: the backend parser must be live
             BEFORE this file is, or every beaconed batch is answered 400
             invalid_ids. The fetch fallback still covers a browser without
             sendBeacon, and requeueOnce() still covers a failed fetch. */
          var blob = new Blob([json], { type: 'text/plain;charset=UTF-8' });
          beaconed = navigator.sendBeacon(BATCH_ENDPOINT, blob);
        }
      } catch (e) { beaconed = false; }
    }
    if (beaconed) return;
    try {
      fetch(BATCH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: json,
        keepalive: true,
        mode: 'cors',
        credentials: 'omit'
      }).catch(function () { requeueOnce(batch); });
    } catch (e) { requeueOnce(batch); }
  }

  // Exactly one queued re-attempt per event (task spec). Events already
  // retried are dropped rather than looping forever.
  function requeueOnce(batch) {
    try {
      var back = [];
      for (var i = 0; i < batch.length; i++) {
        var e = batch[i];
        if (!e.__retried) { e.__retried = 1; back.push(e); }
      }
      if (back.length) queue = back.concat(queue);
    } catch (e) {}
  }

  /* ---------- cart snapshot + interception ---------- */
  var snapshot = {};          // variant_id -> { qty, product_id, sku, grams }
  var snapshotSeeded = false;

  function isGift(vid, sku) {
    try {
      if (sku && String(sku).toUpperCase() === GIFT_SKU) return true;
      var n = (typeof vid === 'number') ? vid : parseInt(vid, 10);
      return GIFT_VARIANT_SET[n] === 1;
    } catch (e) { return false; }
  }

  function cartToMap(cart) {
    var m = {};
    try {
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it || !it.variant_id) continue;
        var prev = m[it.variant_id];
        m[it.variant_id] = {
          qty: (prev ? prev.qty : 0) + (it.quantity || 0),
          product_id: it.product_id,
          sku: it.sku,
          grams: it.grams
        };
      }
    } catch (e) {}
    return m;
  }

  function seedSnapshot(cart) { snapshot = cartToMap(cart); snapshotSeeded = true; }

  function diffCart(cart) {
    var neu = cartToMap(cart);
    if (!snapshotSeeded) { snapshot = neu; snapshotSeeded = true; return; }
    var keys = {}, k;
    for (k in snapshot) keys[k] = 1;
    for (k in neu) keys[k] = 1;
    for (k in keys) {
      if (!keys.hasOwnProperty(k)) continue;
      var oldRec = snapshot[k], newRec = neu[k];
      var oldQ = oldRec ? oldRec.qty : 0;
      var newQ = newRec ? newRec.qty : 0;
      var delta = newQ - oldQ;
      if (delta === 0) continue;
      var rec = newRec || oldRec || {};
      if (isGift(k, rec.sku)) continue; // skip gift auto-mutations
      var vidNum = parseInt(k, 10);
      if (newQ === 0) {
        track('remove_from_cart', { product_id: rec.product_id, variant_id: vidNum, sku: rec.sku || null, qty: 0, qty_delta: delta, grams: rec.grams || null });
      } else {
        track('cart_qty_change', { product_id: rec.product_id, variant_id: vidNum, sku: rec.sku || null, qty: newQ, qty_delta: delta, grams: rec.grams || null });
      }
    }
    snapshot = neu;
    snapshotSeeded = true;
  }

  /* ---------- TASK-4916: cart token — the Purchase join key ----------
     /cart.js answers with token = "<cart_token>?key=<hmac>" once the cart has
     actually been persisted (an empty, never-saved cart still returns the old
     32-hex form). The part BEFORE "?key=" is the same value Shopify puts on
     the resulting order as `order.cart_token` (Admin REST, orders/* webhooks),
     which is what lets a later report join "reached delivery" / "added from
     the delivery upsell" / "handed off to checkout" to a real purchase WITHOUT
     inventing a correlation mechanism.

     It is this browser's own cart id and it is sent only to our own
     /api/track/batch. Nothing is rendered, no cart attribute is written, no
     line-item property is added, and the shopper's UI is untouched.

     We never fetch anything to learn it: the value is read off cart responses
     the page was making anyway, through the fetch wrapper installed below. */
  var cartToken = null;
  var cartTokenWaiters = [];

  function normalizeCartToken(t) {
    if (!t || typeof t !== 'string') return null;
    var bare = t.split('?')[0].trim();
    return bare || null;
  }

  function setCartToken(t) {
    try {
      var v = normalizeCartToken(t);
      if (!v || v === cartToken) return;
      cartToken = v;
      var ws = cartTokenWaiters;
      cartTokenWaiters = [];
      for (var i = 0; i < ws.length; i++) { try { ws[i](cartToken); } catch (e) {} }
    } catch (e) {}
  }

  // Call cb with the cart token as soon as any cart response has revealed it,
  // or with null after timeoutMs if no cart read happened on this page. Fires
  // EXACTLY once. It is a purely analytical wait: it gates nothing the shopper
  // can see and delays no page, cart or checkout work.
  function withCartToken(cb, timeoutMs) {
    try {
      if (cartToken) { cb(cartToken); return; }
      var done = false;
      var fire = function (t) {
        if (done) return;
        done = true;
        try { cb(t || null); } catch (e) {}
      };
      cartTokenWaiters.push(fire);
      setTimeout(function () { fire(cartToken); }, timeoutMs || 2500);
    } catch (e) {}
  }

  function respOk(resp) { return resp && (resp.ok || (resp.status >= 200 && resp.status < 300)); }

  function handleAddResp(resp) {
    if (!respOk(resp)) return;
    resp.clone().json().then(function (data) {
      if (!data) return;
      var items = (data.items && data.items.length) ? data.items : [data];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it || !it.variant_id) continue;
        // best-effort snapshot seed increment (self-heals from full-cart responses)
        var prev = snapshot[it.variant_id];
        snapshot[it.variant_id] = {
          qty: (prev ? prev.qty : 0) + (it.quantity || 1),
          product_id: it.product_id, sku: it.sku, grams: it.grams
        };
        if (isGift(it.variant_id, it.sku)) continue; // skip gift auto-add
        track('add_to_cart', {
          product_id: it.product_id, variant_id: it.variant_id,
          sku: it.sku || null, qty: it.quantity || 1, grams: it.grams || null
        });
      }
    }).catch(function () {});
  }

  function handleCartMutationResp(resp) {
    if (!respOk(resp)) return;
    resp.clone().json().then(function (cart) { setCartToken(cart && cart.token); /* TASK-4916 */ diffCart(cart); }).catch(function () {});
  }

  function handleCartReadResp(resp) {
    if (!respOk(resp)) return;
    resp.clone().json().then(function (cart) { setCartToken(cart && cart.token); /* TASK-4916 */ seedSnapshot(cart); }).catch(function () {});
  }

  function urlOf(input) {
    try {
      if (typeof input === 'string') return input;
      if (input && typeof input.url === 'string') return input.url;
      if (input && typeof input.toString === 'function') return input.toString();
    } catch (e) {}
    return '';
  }

  function methodOf(input, init) {
    try {
      if (init && init.method) return String(init.method).toUpperCase();
      if (input && input.method) return String(input.method).toUpperCase();
    } catch (e) {}
    return 'GET';
  }

  function installFetchWrapper() {
    try {
      var priorFetch = window.fetch;
      if (typeof priorFetch !== 'function') return;
      window.fetch = function (input, init) {
        // Chain FIRST — never let our bookkeeping delay or alter the call.
        var p = priorFetch.apply(this, arguments);
        try {
          var url = urlOf(input);
          if (url) {
            if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
              p.then(handleAddResp).catch(function () {});
            } else if (/\/cart\/(change|update)(\.js)?(\?|$)/.test(url)) {
              p.then(handleCartMutationResp).catch(function () {});
            } else if (/\/cart(\.js)?(\?|$)/.test(url) && methodOf(input, init) === 'GET') {
              p.then(handleCartReadResp).catch(function () {});
            }
          }
        } catch (e) {}
        return p;
      };
    } catch (e) {}
  }

  // Fallback for non-AJAX <form action="/cart/add"> submits. Dawn product
  // forms are AJAX (<product-form> → fetch add.js, caught above), so we only
  // fire here for forms that will actually navigate — otherwise we would
  // double-count the AJAX adds.
  function bindCartFormFallback() {
    document.addEventListener('submit', function (ev) {
      try {
        var form = ev.target;
        if (!form || form.tagName !== 'FORM') return;
        var action = form.getAttribute('action') || '';
        if (!/\/cart\/add/.test(action)) return;
        if (form.closest && form.closest('product-form')) return; // AJAX path handled by wrapper
        var idEl = form.querySelector('[name="id"]');
        var qtyEl = form.querySelector('[name="quantity"]');
        var vid = idEl ? parseInt(idEl.value, 10) : null;
        if (!vid) return;
        var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
        if (isGift(vid)) return;
        track('add_to_cart', { variant_id: vid, qty: qty }, { beacon: true });
      } catch (e) {}
    }, true);
  }

  /* ---------- checkout_started ---------- */
  var checkoutStartedSent = false;
  /* ---- TASK-5104 — the checkout step, finally measurable ----------------
     TASK-4927 established that checkout_started has never been recorded. Two
     independent reasons, both fixed here:

       1. TRANSPORT. The beacon was dropped mid-navigation — see the note in
          send() above. Nothing about the trigger was ever wrong.
       2. NO TRIGGER ON THE REAL HAND-OFF. Since TASK-3340 the cart's own
          Checkout button does NOT go to Shopify checkout: it is gated and
          redirects to the delivery step. The button that actually hands off to
          Shopify is the delivery page's `<button data-ils-next>`, and it
          matches NONE of bindCheckout()'s selectors (it is not #Cart*-Checkout,
          carries no name="checkout" and is not an <a href>). So even with a
          working transport the funnel's real checkout step had no emitter.

     Both are now recorded under the ONE whitelisted name, told apart by
     payload.source, because the backend EVENT_TYPES enum is frozen and this
     task must not widen it again:

       source = 'delivery_next' — THE funnel step. Delivery -> Shopify checkout.
       source = 'cart_gate'     — the cart/drawer button, i.e. cart -> delivery
                                  step. Semantically the same moment as
                                  delivery_reached, kept because it is the
                                  pre-existing behaviour of this trigger.
       source = 'other'         — any other checkout control or form submit.

     `checkoutStartedSent` is unchanged: one event per page load. The cart page
     and the delivery page are separate loads, so a normal journey produces one
     'cart_gate' row and one 'delivery_next' row, and the funnel step is
     measured by the latter alone.

     cart_token is read SYNCHRONOUSLY (never awaited): the page is navigating
     and there is no time to wait for a cart read. On the delivery step the
     token is already known — that page reads /cart.js on load and the fetch
     wrapper captures it — so in practice it is present exactly where the
     funnel needs it. */
  // Mirrors the delivery selector's own `cartItemCount === 0` rule, which
  // bounces an empty cart back to /cart instead of going to checkout: a
  // CONFIRMED-empty cart is not a checkout hand-off. Unknown fails OPEN, so a
  // page that never read the cart still reports the step.
  function cartLooksEmpty() {
    try {
      if (!snapshotSeeded) return false;
      var total = 0;
      for (var k in snapshot) {
        if (!Object.prototype.hasOwnProperty.call(snapshot, k)) continue;
        total += (snapshot[k] && snapshot[k].qty) || 0;
      }
      return total === 0;
    } catch (e) { return false; }
  }
  var DELIVERY_STEP = ((PAGE.template || '') === 'page.delivery-selector');
  function fireCheckoutStarted(source) {
    if (checkoutStartedSent) return;
    checkoutStartedSent = true;
    track('checkout_started', {
      source: source || 'other',
      template: PAGE.template || '',
      cart_token: cartToken
    }, { beacon: true });
  }
  function bindCheckout() {
    document.addEventListener('click', function (ev) {
      try {
        var t = ev.target;
        if (!t || typeof t.closest !== 'function') return;
        var btn = t.closest('#CartDrawer-Checkout, #CartPage-Checkout, [name="checkout"], button[name="checkout"], a[href*="/checkout"]');
        if (btn) { fireCheckoutStarted(/* TASK-5104 */ (btn.id === 'CartPage-Checkout' || btn.id === 'CartDrawer-Checkout') ? 'cart_gate' : 'other'); return; }
        /* TASK-5104 — THE DELIVERY -> SHOPIFY CHECKOUT HAND-OFF.
           Observed from this already-installed document CAPTURE listener, so
           assets/custom-delivery-selector.js is not opened at all and the Next
           click path stays exactly what TASK-4945 made it: dedupe -> bridge
           beacon -> location.assign(checkout), awaiting nothing. We run BEFORE
           the selector's own handler, we never preventDefault, never
           stopPropagation and never await — track() queues and hands the batch
           to navigator.sendBeacon, which is fire-and-forget. Next stays as fast
           as it is today.

           Gated on the TEMPLATE so the attribute cannot be borrowed by some
           other page later, and on [data-ils-next] rather than a class so a
           restyle cannot silently kill the event. A disabled <button> dispatches
           no click at all, so an incomplete selection produces nothing — the
           same condition the selector itself enforces. */
        if (DELIVERY_STEP && t.closest('[data-ils-next]') && !cartLooksEmpty()) {
          fireCheckoutStarted('delivery_next');
        }
      } catch (e) {}
    }, true);
    document.addEventListener('submit', function (ev) {
      try {
        var form = ev.target;
        if (!form || form.tagName !== 'FORM') return;
        var action = form.getAttribute('action') || '';
        if (/\/checkout/.test(action) || form.querySelector('[name="checkout"]')) fireCheckoutStarted();
      } catch (e) {}
    }, true);
  }

  /* ---------- page_leave (pagehide / visibilitychange→hidden) ---------- */
  var pageStart = Date.now();
  var maxScrollPct = 0;
  var lastScrollCalc = 0;
  function calcScroll() {
    try {
      var now = Date.now();
      if (now - lastScrollCalc < 500) return; // throttle >=500ms
      lastScrollCalc = now;
      var doc = document.documentElement, body = document.body || {};
      var scrollTop = window.pageYOffset || doc.scrollTop || 0;
      var vh = window.innerHeight || doc.clientHeight || 0;
      var full = Math.max(body.scrollHeight || 0, doc.scrollHeight || 0, body.offsetHeight || 0, doc.offsetHeight || 0, doc.clientHeight || 0);
      if (full <= 0) return;
      var pct = Math.round(((scrollTop + vh) / full) * 100);
      if (pct > 100) pct = 100; if (pct < 0) pct = 0;
      if (pct > maxScrollPct) maxScrollPct = pct;
    } catch (e) {}
  }
  function bindScroll() {
    try { window.addEventListener('scroll', calcScroll, { passive: true }); } catch (e) {
      try { window.addEventListener('scroll', calcScroll); } catch (e2) {}
    }
    calcScroll();
  }

  var leaveSent = false;
  function onLeave() {
    try {
      if (!leaveSent) {
        leaveSent = true;
        track('page_leave', { time_on_page_ms: Date.now() - pageStart, scroll_depth_pct: maxScrollPct });
      }
      flush(true); // beacon-flush whatever is queued
    } catch (e) {}
  }
  function bindLeave() {
    window.addEventListener('pagehide', onLeave);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') onLeave();
    });
  }

  /* ---------- page-level events ---------- */
  function handleLocaleSwitch() {
    try {
      var prev = null;
      try { prev = sessionStorage.getItem('ils_trk_prev_locale'); } catch (e) {}
      if (prev && CUR_LOCALE && prev !== CUR_LOCALE) {
        track('locale_switch', { from: prev, to: CUR_LOCALE });
      }
      if (CUR_LOCALE) { try { sessionStorage.setItem('ils_trk_prev_locale', CUR_LOCALE); } catch (e) {} }
    } catch (e) {}
  }

  var pageEventsFired = false;
  function firePageEvents() {
    if (pageEventsFired) return;
    pageEventsFired = true;
    var pt = PAGE.page_type || '';
    var tmpl = PAGE.template || '';
    track('page_view', { template: tmpl, page_type: pt });
    if (PAGE.product && (pt === 'product' || tmpl.indexOf('product') === 0)) {
      var pr = PAGE.product;
      track('product_view', {
        product_id: pr.id, variant_id: pr.variant_id, handle: pr.handle,
        title: pr.title, sku: pr.sku || null, grams: pr.grams || null
      });
    }
    if (PAGE.collection && (pt === 'collection' || tmpl.indexOf('collection') === 0)) {
      var co = PAGE.collection;
      track('collection_view', { collection_id: co.id, handle: co.handle, title: co.title });
    }
    /* ---- TASK-4916 — DeliveryReached -------------------------------------
       The funnel step the store was missing:

           add_to_cart -> delivery_reached -> checkout_started -> Purchase

       It fires when the shopper actually LOADS the delivery step — the page
       the cart's "Next" gate sends them to (TASK-3340: #CartPage-Checkout /
       #CartDrawer-Checkout carry data-ils-checkout-gate and redirect here),
       and the only page whose own Next hands off to Shopify checkout.

       GATE = THE TEMPLATE, NEVER THE HANDLE. This store translates page
       handles (the delivery page is /pages/vybor-dostavki on ru), so a handle
       gate would silently lose every non-English market. The suffix is not
       translatable. The comparison is exact so the hidden prototype page
       (template page.delivery-upsell-test) can never match.

       COUNTING — the same principle the AddToCart funnel metric already uses.
       This rides pageEventsFired, so it is emitted exactly as often as this
       page's own page_view: once per real page load, and NOT on a bfcache
       back/forward restore (which re-runs no script at all). Nothing is
       de-duplicated client side, exactly like add_to_cart, which is written on
       every add; the collapsing happens at query time, where ils-admin's
       countTrackerCartUsers() does COUNT(DISTINCT visitor_id, Riga calendar
       day). The identical DISTINCT over delivery_reached therefore yields
       people-per-day, comparable 1:1 with the AddToCart tile, while the raw
       rows stay available for a per-visit view.

       TRANSPORT: {immediate:true} = flush now over the ordinary keepalive
       fetch. No navigation is in progress when this fires, so the beacon
       question does not arise here at all.

       The cart token (join key to order.cart_token) is read off the /cart.js
       call the delivery page already makes; the 2.5s ceiling means a page that
       never reads the cart still reports the step, just without the key. */
    if (tmpl === 'page.delivery-selector') {
      var dsFromCart = false;
      try { dsFromCart = /\/cart(\.js)?(\?|#|$)/.test(document.referrer || ''); } catch (e) {}
      withCartToken(function (tok) {
        track('delivery_reached', {
          template: tmpl,
          cart_token: tok,
          from_cart: dsFromCart
        }, { immediate: true });
      }, 2500);
    }

    handleLocaleSwitch();
  }

  /* ---------- init (only after consent) ---------- */
  var initialized = false;
  function init() {
    if (initialized) return;
    initialized = true;
    try {
      ensureVisitorId();
      ensureSession();
      installFetchWrapper();
      bindLeave();
      bindScroll();
      bindCartFormFallback();
      bindCheckout();
      flushTimer = setInterval(function () { flush(false); }, FLUSH_INTERVAL_MS);
      firePageEvents();
    } catch (e) {}
  }

  function startWhenReady() {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
  }

  /* ---------- public API (always available, consent-independent) ----------
     identify() is a direct response to an explicit email submission (the
     visitor handed us their address), so it is exposed regardless of the
     analytics consent gate. It is fire-and-forget and never throws. */
  window.ILSTracker = {
    getVisitorId: function () { try { return ensureVisitorId(); } catch (e) { return null; } },
    identify: function (email, source) {
      try {
        if (!email) return;
        var vid = ensureVisitorId();
        var json = JSON.stringify({ visitor_id: vid, email: String(email), source: source || 'unknown' });
        if (navigator.sendBeacon) {
          try {
            var blob = new Blob([json], { type: 'application/json' });
            if (navigator.sendBeacon(IDENTIFY_ENDPOINT, blob)) return;
          } catch (e) {}
        }
        fetch(IDENTIFY_ENDPOINT, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: json, keepalive: true, mode: 'cors', credentials: 'omit'
        }).catch(function () {});
      } catch (e) {}
    },
    // TASK-4916 — the third argument passes the transport options straight
    // through ({immediate:true} = flush now, over the plain keepalive fetch).
    // Existing two-argument callers behave exactly as before.
    track: function (type, payload, opts) { try { if (initialized) track(type, payload, opts); } catch (e) {} },
    // TASK-4916 — the bare Shopify cart token this tab has observed (no
    // "?key=" suffix), or null. Read-only; it is the join key to
    // order.cart_token and is never rendered anywhere.
    getCartToken: function () { try { return cartToken; } catch (e) { return null; } }
  };

  /* ---------- consent wiring ---------- */
  // Fires when ils-consent.js (or any future banner) dispatches ils:consent.
  document.addEventListener('ils:consent', function (ev) {
    try {
      var c = ev && ev.detail;
      if (c && c.analytics === true) {
        if (!initialized) init();
        // consent_changed is meaningful only once analytics is granted.
        track('consent_changed', {
          necessary: !!(c && c.necessary),
          analytics: !!(c && c.analytics),
          marketing: !!(c && c.marketing)
        });
      }
    } catch (e) {}
  });

  // TASK-2699 (temp): collect UNCONDITIONALLY — owner removed the consent
  // banner (TASK-2553) and instructed the tracker to start on every load,
  // regardless of ils_consent. The ils:consent listener above stays in place
  // (harmless) but is no longer the load-time trigger.
  startWhenReady();
})();
